import React, { useCallback, useEffect, useState } from 'react';
import Card from 'components/inputs/Card';
import { Spinner, Modal } from 'react-bootstrap';
import Button from 'components/inputs/Button';
import * as _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { UPDATE_USER } from 'actions/user';
import { useParams } from 'react-router';
import { UPDATE_STATUS_CODE } from 'components/common/constant';
import { toast } from 'react-toastify';
import { FETCH_CUSTOMER_PROFILE } from 'actions/customer';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import CheckBox from 'components/inputs/CheckBox';
import RequestModal from '../partnerManagement/EMSP/RequestModal';
import AddPrivateStationModal from './AddPrivateStationModal';
import AddPublicStationModal from './AddPublicStationModal';

const FleetStationAccess = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { fleetId } = useParams();

  // removeTarget tells the confirm modal which list it is acting on: 'private' | 'public' | null
  const [removeTarget, setRemoveTarget] = useState(null);
  const [showAddPrivateModal, setShowAddPrivateModal] = useState(false);
  const [showAddPublicModal, setShowAddPublicModal] = useState(false);

  const userDetail = useSelector((state) => state.customerProfile.customerProfile);
  const isLoader = useSelector((state) => state.customerProfile.isLoading);

  const [privateList, setPrivateList] = useState([]);
  const [publicList, setPublicList] = useState([]);

  // Separate selection maps so private and public ids never collide.
  const [privateSelection, setPrivateSelection] = useState({});
  const [publicSelection, setPublicSelection] = useState({});
  const [privateSelectAll, setPrivateSelectAll] = useState(false);
  const [publicSelectAll, setPublicSelectAll] = useState(false);

  // charging_access_on_stations can be the literal string 'All' (access to every station).
  const publicRaw = userDetail?.charging_access_on_stations;
  const publicIsAll = publicRaw === 'All';

  // Sync local lists whenever the redux customer doc changes (initial load + manual refetch).
  useEffect(() => {
    setPrivateList(userDetail?.allowed_private_stations || []);
    setPublicList(publicIsAll ? [] : _.isArray(publicRaw) ? publicRaw : []);
  }, [userDetail, publicIsAll, publicRaw]);

  const getCustomerDetail = useCallback(
    (id) => {
      dispatch({ type: FETCH_CUSTOMER_PROFILE.REQUEST, payload: id });
    },
    [dispatch]
  );

  useEffect(() => {
    getCustomerDetail(fleetId);
  }, [fleetId, getCustomerDetail]);

  // Persist either access field. `field` is the customer-doc key being written.
  const persistList = useCallback(
    (field, nextStations, successMessage) => {
      const ids = _.map(nextStations, (s) => (s?.id ? s.id : s));
      dispatch({
        type: UPDATE_USER.REQUEST,
        payload: { [field]: ids, id: fleetId },
        cb: (res) => {
          if (_.get(res, 'status') === UPDATE_STATUS_CODE) {
            toast.success(successMessage);
            setPrivateSelection({});
            setPublicSelection({});
            setPrivateSelectAll(false);
            setPublicSelectAll(false);
            // refresh redux in background; UI already reflects new list
            getCustomerDetail(fleetId);
          }
        },
      });
    },
    [dispatch, fleetId, getCustomerDetail]
  );

  // Keep each section's "select all" flag in sync with its rows.
  useEffect(() => {
    setPrivateSelectAll(!_.isEmpty(privateList) && privateList.every((s) => privateSelection[s.id]?.stationChecked));
  }, [privateList, privateSelection]);

  useEffect(() => {
    setPublicSelectAll(!_.isEmpty(publicList) && publicList.every((s) => publicSelection[s.id]?.stationChecked));
  }, [publicList, publicSelection]);

  const handleSelectAll = (list, selectAll, setSelection, setSelectAll) => () => {
    const next = !selectAll;
    setSelectAll(next);
    const updated = {};
    list.forEach((s) => {
      updated[s.id] = { stationChecked: next };
    });
    setSelection((prev) => ({ ...prev, ...updated }));
  };

  const handleStationSelect = (setSelection) => (stationId) => {
    setSelection((prev) => ({
      ...prev,
      [stationId]: { stationChecked: !prev[stationId]?.stationChecked },
    }));
  };

  const getSelectedIds = (selection) =>
    Object.entries(selection)
      .filter((entry) => entry[1].stationChecked)
      .map((entry) => entry[0]);

  const isAnySelected = (selection) => Object.values(selection).some((s) => s.stationChecked);

  const handleAddPrivateStations = (newStations) => {
    setPrivateList((prev) => {
      const existingIds = new Set(prev.map((s) => s.id));
      const additions = newStations.filter((s) => !existingIds.has(s.id));
      const next = [...prev, ...additions];
      persistList('allowed_private_stations', next, 'Private station(s) granted');
      return next;
    });
    setShowAddPrivateModal(false);
  };

  const handleAddPublicStations = (newStations) => {
    setPublicList((prev) => {
      const existingIds = new Set(prev.map((s) => s.id));
      const additions = newStations.filter((s) => !existingIds.has(s.id));
      const next = [...prev, ...additions];
      persistList('charging_access_on_stations', next, 'Public station(s) granted');
      return next;
    });
    setShowAddPublicModal(false);
  };

  const handleConfirmRemove = () => {
    if (removeTarget === 'private') {
      const selectedIds = getSelectedIds(privateSelection);
      const remaining = privateList.filter((s) => !selectedIds.includes(s.id));
      setPrivateList(remaining);
      persistList('allowed_private_stations', remaining, 'Remove Station(s) successfully');
    } else if (removeTarget === 'public') {
      const selectedIds = getSelectedIds(publicSelection);
      const remaining = publicList.filter((s) => !selectedIds.includes(s.id));
      setPublicList(remaining);
      persistList('charging_access_on_stations', remaining, 'Remove Station(s) successfully');
    }
    setRemoveTarget(null);
  };

  const loading = isLoader || _.isUndefined(isLoader);

  return (
    <React.Fragment>
      <div className="fsa-tab">
        <Card>
          <StationSection
            title={'Private Stations'}
            stations={privateList}
            loading={loading}
            selection={privateSelection}
            selectAll={privateSelectAll}
            onSelectAll={handleSelectAll(privateList, privateSelectAll, setPrivateSelection, setPrivateSelectAll)}
            onStationSelect={handleStationSelect(setPrivateSelection)}
            onRemove={() => setRemoveTarget('private')}
            removeDisabled={!isAnySelected(privateSelection)}
            removeLabel={'Remove Private Station'}
            headerExtra={
              <Button onClick={() => setShowAddPrivateModal(true)} className="fsa__add-btn">
                {'+ Add Private Station'}
              </Button>
            }
            t={t}
          />

          <StationSection
            title={'Public Stations'}
            stations={publicList}
            loading={loading}
            isAll={publicIsAll}
            selection={publicSelection}
            selectAll={publicSelectAll}
            onSelectAll={handleSelectAll(publicList, publicSelectAll, setPublicSelection, setPublicSelectAll)}
            onStationSelect={handleStationSelect(setPublicSelection)}
            onRemove={() => setRemoveTarget('public')}
            removeDisabled={!isAnySelected(publicSelection)}
            removeLabel={'Remove Public Station'}
            headerExtra={
              !publicIsAll && (
                <Button onClick={() => setShowAddPublicModal(true)} className="fsa__add-btn">
                  {'+ Add Public Station'}
                </Button>
              )
            }
            t={t}
          />
        </Card>
      </div>

      <AddPrivateStationModal
        show={showAddPrivateModal}
        onClose={() => setShowAddPrivateModal(false)}
        onSubmit={handleAddPrivateStations}
      />

      <AddPublicStationModal
        show={showAddPublicModal}
        onClose={() => setShowAddPublicModal(false)}
        onSubmit={handleAddPublicStations}
        existingIds={publicList.map((s) => (s?.id ? s.id : s))}
      />

      {removeTarget && (
        <Modal show={!!removeTarget} centered onHide={() => setRemoveTarget(null)}>
          <RequestModal
            subTitle={t('stationAccessToPatner.modelTitle')}
            title={t('stationAccessToPatner.modelTitle')}
            onClose={() => setRemoveTarget(null)}
            requestModal={handleConfirmRemove}
          />
        </Modal>
      )}
    </React.Fragment>
  );
};

const formatLocation = (station) => {
  const address = (station.address || '').trim();
  if (address) return address;
  return (
    [station.area, station.city, station.state, station.pincode]
      .map((v) => (v == null ? '' : String(v)).trim())
      .filter(Boolean)
      .join(', ') || '—'
  );
};

const StationSection = ({
  title,
  stations,
  loading,
  isAll,
  selection,
  selectAll,
  onSelectAll,
  onStationSelect,
  onRemove,
  removeDisabled,
  removeLabel,
  headerExtra,
  t,
}) => {
  const count = isAll ? '—' : stations.length;
  const showControls = !isAll && !_.isEmpty(stations) && !loading;

  return (
    <div className="fsa__section">
      <div className="fsa__toolbar">
        <span className="fsa__count">{`${title} (${count})`}</span>
        {showControls && (
          <div className="fsa__select-all">
            <label>{t('stationAccessToPatner.selectAll')}</label>
            <CheckBox checked={selectAll} onChange={onSelectAll} />
          </div>
        )}
        {headerExtra}
      </div>

      <div className="station-access-list__main">
        <div className="station-access-list--block">
          {loading ? (
            <div className="loader--block">
              <Spinner as="span" animation="border" size="sm" role="status" />
              <span className="ps-2">{t('placeHolder.loading')}</span>
            </div>
          ) : isAll ? (
            <div className="fsa__all-row">
              <div className="station--label">{'✓ All Stations'}</div>
              <div className="charger--label">{'Manage via the Station Access menu'}</div>
            </div>
          ) : _.isEmpty(stations) ? (
            <div className="empty-data-block">{t('stationAccessToPatner.noData')}</div>
          ) : (
            _.map(stations, (item) => (
              <ChargerDataItem
                key={item.id}
                station={item}
                stationChecked={selection[item.id]?.stationChecked || false}
                onStationSelect={() => onStationSelect(item.id)}
              />
            ))
          )}
        </div>
      </div>

      {showControls && (
        <div className="charger-list--button fsa__remove-row">
          <Button onClick={onRemove} disabled={removeDisabled} className="fsa__remove-btn">
            {removeLabel}
          </Button>
        </div>
      )}
    </div>
  );
};

StationSection.propTypes = {
  title: PropTypes.string,
  stations: PropTypes.array,
  loading: PropTypes.bool,
  isAll: PropTypes.bool,
  selection: PropTypes.object,
  selectAll: PropTypes.bool,
  onSelectAll: PropTypes.func,
  onStationSelect: PropTypes.func,
  onRemove: PropTypes.func,
  removeDisabled: PropTypes.bool,
  removeLabel: PropTypes.string,
  headerExtra: PropTypes.node,
  t: PropTypes.func,
};

const ChargerDataItem = ({ station, stationChecked, onStationSelect }) => {
  return (
    <div className="station-access--list" onClick={onStationSelect}>
      <div className="list-info">
        <div className="station--label">{station.name || '—'}</div>
        <div className="charger--label">{formatLocation(station)}</div>
      </div>
      <span onClick={(e) => e.stopPropagation()}>
        <CheckBox checked={stationChecked} onChange={onStationSelect} />
      </span>
    </div>
  );
};

ChargerDataItem.propTypes = {
  station: PropTypes.object,
  onStationSelect: PropTypes.func,
  stationChecked: PropTypes.bool,
};

export default FleetStationAccess;
